import { FC } from 'react';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import {
  numberToXDecimalPlaces,
  percentageToNdecialPlaces,
  roundToTwoDecimalPlacesReturnNumber,
  toFourDecimalPlacesIfNumber,
} from '../../../../utilities/numberFormatters';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import NoDataMessage from '../../../feedback/NoDataMessage.component';
import { RaptorTheme, mainColors } from '../../../../styling/theme';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  XAxis,
  YAxis,
} from 'recharts';
import makeStyles from '@mui/styles/makeStyles';
import { hexToRGBA } from '../../../../utilities/colorUtilities';
import type {
  LiquidationCostData,
  VariableMarketVolumeLiquidationCostsReport,
} from './variableMarketVolumeLiquidationCosts.types';
import { getStressTestLiquidationCostData } from './variableMarketVolumeLiquidationCosts.types';

interface VariableMarketVolumeLiquidationCostsTablesProps {
  data: VariableMarketVolumeLiquidationCostsReport;
  selectedScenario: string | null;
}

interface IndividualTableProps {
  liquidationCostData: LiquidationCostData | null;
  title: string[];
  scenario: string | null;
}

interface IIndividualTableData {
  navChange: number;
  bau: number;
  bauElevated: number;
  bauStressed: number;
  elevated: number;
  elevatedStressed: number;
  stressed: number;
}

function getScenarioNavChange(scenario: string | null) {
  switch (scenario) {
    case 'redemption_liquidation_cost_4_5':
      return 0.05;
    case 'redemption_liquidation_cost_20_1':
      return 0.2;
    case 'redemption_liquidation_cost_20w_5':
      return 0.2;
    default:
      return 0;
  }
}

function buildIndividualTableData(
  lcd: LiquidationCostData | null,
  key: string,
  scenario: string | null,
): IIndividualTableData[] {
  if (!lcd || !scenario) return [];
  if (!(key in lcd)) return [];
  const scenarioData = lcd[key as keyof LiquidationCostData];
  if (!Array.isArray(scenarioData)) return [];
  const scenarioDataNoHeader = scenarioData.slice(1) as number[][];
  const tableData: IIndividualTableData[] = [];

  scenarioDataNoHeader.forEach((value: number[], index: number) => {
    tableData.push({
      navChange: getScenarioNavChange(scenario) * (index + 1),
      bau: value[0],
      bauElevated: value[1],
      bauStressed: value[2],
      elevated: value[3],
      elevatedStressed: value[4],
      stressed: value[5],
    });
  });
  return tableData;
}

type GraphChartType = { [index: string]: number };

const buildChartData = (
  lcd: LiquidationCostData | null,
  dataKeys: string[][],
  scenario: string | null,
) => {
  const chartData: { [index: string]: GraphChartType[] } = {};
  if (!lcd || !scenario) return chartData;
  dataKeys.forEach((key) => {
    if (!(key[1] in lcd)) return;
    const scenarioData = lcd[key[1] as keyof LiquidationCostData];
    if (!Array.isArray(scenarioData)) return;
    const scenarioDataNoHeader = scenarioData.slice(1) as number[][];
    const newChart: GraphChartType[] = [];
    scenarioDataNoHeader.forEach((group: number[], index: number) => {
      const newGroup: GraphChartType = {
        group: roundToTwoDecimalPlacesReturnNumber(
          getScenarioNavChange(scenario) * (index + 1),
        ),
      };
      group.forEach((item: number, index: number) => {
        newGroup[scenarioData[0][index]] = numberToXDecimalPlaces(item, 3);
      });
      newChart.push(newGroup);
    });
    chartData[key[1]] = newChart;
  });
  return chartData;
};

const detailColumns: CustomColumn<IIndividualTableData>[] = [
  {
    title: 'Nav Change',
    field: 'navChange',
    cellStyle: {
      textAlign: 'center',
      borderRight: `1px solid ${mainColors.mainBlue}`,
    },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => '-' + percentageToNdecialPlaces(rowData.navChange, 0),
  },
  {
    title: 'BAU/BAU',
    field: 'bau',
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => percentageToNdecialPlaces(rowData.bau, 3),
  },
  {
    title: 'BAU/Elevated',
    field: 'bauElevated',
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => percentageToNdecialPlaces(rowData.bauElevated, 3),
  },
  {
    title: 'Elevated/Elevated',
    field: 'elevated',
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => percentageToNdecialPlaces(rowData.elevated, 3),
  },
  {
    title: 'Elevated/Stressed',
    field: 'elevatedStressed',
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => percentageToNdecialPlaces(rowData.elevatedStressed, 3),
  },
  {
    title: 'Stressed/Stressed',
    field: 'stressed',
    cellStyle: { textAlign: 'center' },
    headerStyle: { textAlign: 'center' },
    render: (rowData) => percentageToNdecialPlaces(rowData.stressed, 3),
  },
];

const IndividualTable: FC<IndividualTableProps> = (props) => {
  const { liquidationCostData, title, scenario } = props;
  const tableData = buildIndividualTableData(
    liquidationCostData,
    title[1],
    scenario,
  );

  return (
    <>
      <CustomTable<IIndividualTableData>
        pdfTitle={title[0]}
        id={title[0]}
        columns={detailColumns}
        showToolbar
        data={tableData}

        title={title[0]}
        options={{
          paging: false,
          search: false,
          exportButton: true,
        }}
      />
    </>
  );
};

const useStyles = makeStyles<RaptorTheme>((theme) => ({
  dataArea: {
    width: '100%',
    display: 'flex',
    margin: '1.25rem 0',
    '@media only screen and (max-width: 1600px)': {
      flexDirection: 'column',
    },
  },
  card: {
    flex: 1,
    padding: '0.62rem',
    minHeight: '25.00rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  group: {
    height: '3.12rem',
    width: '3.12rem',
    backgroundColor: 'red',
  },
  customLabelContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  customLabelTop: {
    fontSize: '0.81rem',
    fontWeight: 500,
    color: mainColors.mainBlue,
  },
  customLabelBottom: {
    fontSize: '0.81rem',
    fontWeight: 700,
    color: mainColors.mainBlue,
  },
}));

const barChartColours: string[] = [
  '#c5d7ec',
  '#9fbde0',
  '#79a2d3',
  '#5288c6',
  '#396ead',
  '#2c5686',
  '#1f3d60',
];

function getDataKeys(lcd: LiquidationCostData | null): string[][] {
  if (!lcd?.data_keys) return [];
  return lcd.data_keys;
}

interface CustomizedLabelProps {
  title: string | number;
  x: number;
}

const CustomizedLabel: FC<CustomizedLabelProps> = (props) => {
  const classes = useStyles();
  return (
    <g>
      <foreignObject x={props.x} y={100} width={'5.00rem'} height={'3.12rem'}>
        <div className={classes.customLabelContainer}>
          <div className={classes.customLabelTop}>NaV Change:</div>
          <div className={classes.customLabelBottom}>
            -{percentageToNdecialPlaces(props.title, 0)}
          </div>
        </div>
      </foreignObject>
    </g>
  );
};


const VariableMarketVolumeLiquidationCostsTables: FC<
  VariableMarketVolumeLiquidationCostsTablesProps
> = (props) => {
  const classes = useStyles();

  const { data: report, selectedScenario } = props;

  const liquidationCostData = getStressTestLiquidationCostData(
    report,
    selectedScenario,
  );

  const dataKeys: string[][] = getDataKeys(liquidationCostData);

  const chartData = buildChartData(
    liquidationCostData,
    dataKeys,
    selectedScenario,
  );

  return dataKeys.length && liquidationCostData ? (
    <>
      {dataKeys.map((key: string[]) => {
        const series = chartData[key[1]];
        const firstRow = series?.[0];
        return (
          <div className={classes.dataArea} key={key[1]}>
            <GridItem card className={classes.card}>
              <IndividualTable
                liquidationCostData={liquidationCostData}
                title={key}
                scenario={selectedScenario}
              />
            </GridItem>
            <GridItem card className={classes.card}>
              {series?.length && firstRow ? (
                <BarChart
                  width={800}
                  height={400}
                  data={series}
                  margin={{ top: 25, right: 25, left: 0, bottom: 25 }}
                >
                  <XAxis dataKey="group" hide={true} />
                  <YAxis
                    type="number"
                    domain={[
                      0,
                      (dataMax: number) => toFourDecimalPlacesIfNumber(dataMax),
                    ]}
                  />
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <Legend verticalAlign={'top'} height={40} />
                  {Object.keys(firstRow)
                    .filter((bar) => bar !== 'group')
                    .map((bar, index) => (
                      <Bar
                        key={`${key[1]}_${bar}`}
                        dataKey={bar}
                        fill={barChartColours[index % barChartColours.length]}
                      />
                    ))}
                  {series.map((row: GraphChartType, index: number) => (
                    <ReferenceArea
                      key={`ref-${key[1]}-${row.group}-${index}`}
                      x1={row.group}
                      x2={row.group}
                      stroke={hexToRGBA(mainColors.mainBlue, 0.3)}
                      fill={hexToRGBA(mainColors.mainBlue, 0.1)}
                      label={
                        <CustomizedLabel
                          title={row.group}
                          x={
                            80 + (800 / (series.length + 0.6)) * index
                          }
                        />
                      }
                    />
                  ))}
                </BarChart>
              ) : (
                <NoDataMessage />
              )}
            </GridItem>
          </div>
        );
      })}
    </>
  ) : (
    <NoDataMessage />
  );
};

export default VariableMarketVolumeLiquidationCostsTables;
